import { failureCode } from "@aybuben/client/auth/appwrite.ts";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { useStrings, type UiKey } from "../i18n/index.tsx";
import { COL } from "../theme.ts";
import { Btn } from "../ui/primitives.tsx";
import { AUTH_ERROR_KEY, errorKey } from "./copy.ts";
import { LinkBtn } from "./fields.tsx";

interface AuthFailurePageProps {
	/** A guest whose Google link failed, most likely because that Google account already has one. */
	guest: boolean;
	onLeaveGuest: () => Promise<void>;
}

export function AuthFailurePage({ guest, onLeaveGuest }: AuthFailurePageProps) {
	const t = useStrings();
	const navigate = useNavigate();
	const [params] = useSearchParams();
	const [busy, setBusy] = useState(false);
	const [err, setErr] = useState<UiKey | null>(null);
	const code = failureCode(params.get("error"));
	// Only "that account exists" earns the way out: leaving deletes a guest for
	// good, and a cancelled consent screen or a server error is no reason to.
	const offerLeave = guest && code === "emailTaken";
	const leave = () => {
		if (busy) return;
		setBusy(true);
		onLeaveGuest()
			.catch((e: unknown) => setErr(errorKey(e)))
			.finally(() => setBusy(false));
	};
	return (
		<div className="px-6 pt-4 pb-16">
			<h1 className="mt-4 text-2xl font-extrabold">{t("auth.failureTitle")}</h1>
			<p className="mt-2 text-sm leading-relaxed" style={{ color: COL.mute }}>
				{offerLeave ? t("auth.failureGuestBody") : t(AUTH_ERROR_KEY[code])}
			</p>
			{err && (
				<p role="alert" className="mt-3 text-sm font-semibold" style={{ color: COL.red }}>
					{t(err)}
				</p>
			)}
			<div className="mt-6">
				{offerLeave ? (
					<Btn onClick={leave} disabled={busy} color={COL.pom}>
						{t("auth.leaveConfirm")}
					</Btn>
				) : (
					<Btn onClick={() => navigate(guest ? "/" : "/auth", { replace: true })} color={COL.pom}>
						{t("auth.tryAgain")}
					</Btn>
				)}
			</div>
			{offerLeave && (
				<LinkBtn onClick={() => navigate("/", { replace: true })}>{t("auth.back")}</LinkBtn>
			)}
		</div>
	);
}
